import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowClockwise, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { FixInput } from "@/components/leadRescue/FixInput";
import { rescueChanged } from "@/components/layout/useRescueCount";

const BTN = "border-2 border-black px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100 disabled:opacity-40";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// One held-back lead: what's missing, fill-in boxes, then check again and send.
export function RescueRow({ lead, fields, onDone, onUpdate }) {
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const p = `rescue-${lead.id}`;
  const fixable = lead.missing.filter((m) => m.fixable && fields[m.key]);
  const stuck = lead.missing.filter((m) => !m.fixable);
  const run = async (path, body) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/lead-rescue/${lead.id}/${path}`, body);
      rescueChanged();
      if (path === "dismiss") { toast.success("Removed from Lead Rescue"); onDone(lead.id); return; }
      if (r.data.lead) { setNote(r.data.message); onUpdate(r.data.lead); toast.error(r.data.message); return; }
      toast.success(r.data.message);
      onDone(lead.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't do that. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-2 border-black bg-white p-3" data-testid={p}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Link to={`/leads/${lead.id}`} className="font-bold underline" data-testid={`${p}-name`}>{lead.name}</Link>
        <span className="label-mono text-zinc-600">{lead.state || "No state"} · {when(lead.created_at)}</span>
        {lead.buyer && <span className="text-[11px] text-zinc-600" data-testid={`${p}-buyer`}>Buyer page: {lead.buyer}</span>}
        {lead.page && <span className="font-mono text-[10px] text-zinc-500">/{lead.page}</span>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${p}-missing`}>
        {lead.missing.map((m) => (
          <span key={m.key} className={`px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${m.fixable ? "bg-amber-200" : "bg-red-100 text-red-800"}`}>
            Missing: {m.label}
          </span>
        ))}
      </div>
      {fixable.length > 0 && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {fixable.map((m) => (
            <label key={m.key} className="text-[11px]">
              <span className="label-mono block mb-0.5">{fields[m.key].label}</span>
              <FixInput field={m.key} spec={fields[m.key]} value={answers[m.key] || ""} testId={`${p}-fix-${m.key}`}
                        onChange={(v) => setAnswers((a) => ({ ...a, [m.key]: v }))} />
            </label>
          ))}
        </div>
      )}
      {stuck.length > 0 && (
        <p className="mt-2 text-[11px] text-red-800" data-testid={`${p}-stuck`}>
          {stuck.map((m) => m.label).join(", ")} can&apos;t be filled in for them. If the person never agreed to be contacted, this lead can&apos;t be sent.
        </p>
      )}
      {note && <p className="mt-2 text-[11px] font-bold text-amber-900" data-testid={`${p}-note`}>{note}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={() => run("resend", { answers })} data-testid={`${p}-resend`}
                className={`${BTN} bg-black text-white hover:bg-emerald-700`}>
          <ArrowClockwise size={12} weight="bold" /> {busy ? "Checking…" : "Check again & send"}
        </button>
        <button type="button" disabled={busy} onClick={() => run("dismiss")} data-testid={`${p}-dismiss`}
                className={`${BTN} bg-white hover:bg-zinc-100`}>
          <X size={12} weight="bold" /> Dismiss
        </button>
      </div>
    </div>
  );
}
